import {
  CFormCheck,
  CFormInput,
  CModal,
  CModalBody,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import DeleteIcon from "../../components/icons/DeleteIcon";
import EditIcon from "../../components/icons/EditIcon";
import PromptModal from "../../components/modals/PromptModal";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import { tdStyle, thStyle } from "../../helpers/constants";
import { Dash, IconAction } from "../../helpers/general";

const cleanUrl = (url?: string) =>
  url?.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/$/, "");

/* ─────────────── Edit modal ─────────────── */

function EditPartnerModal({
  partner,
  onClose,
}: {
  partner: Partner;
  onClose: () => void;
}) {
  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState(partner.name ?? "");
  const [link, setLink] = useState(partner.link ?? "");
  const logoRef = useRef<HTMLInputElement>(null);
  const darkLogoRef = useRef<HTMLInputElement>(null);

  const update = useMutation((data: any) =>
    apiService.MakePutRequest(`partners/${partner.id}`, data, token, true),
  );

  const save = () =>
    update.mutate(
      {
        name,
        link,
        logo: logoRef.current?.files?.[0] || undefined,
        darkLogo: darkLogoRef.current?.files?.[0] || undefined,
      },
      {
        onError: () => toastError(general.fr.message.operationFailed),
        onSuccess() {
          queryClient.invalidateQueries("getAllPartners");
          queryClient.invalidateQueries("getPartner");
          toastSuccess(general.fr.message.partnerUpdated);
          onClose();
        },
      },
    );

  const hasDark =
    partner.darkLogoImage && !String(partner.darkLogoImage).endsWith("/null");

  return (
    <CModal visible onClose={onClose} alignment="center">
      <CModalBody>
        <h5 className="fs-5 mb-4 border-bottom pb-1">
          Mise à jour du partenaire
        </h5>

        <div className="mb-3">
          <label htmlFor="partner-name" className="text-sm">
            Nom du partenaire
          </label>
          <input
            id="partner-name"
            type="text"
            className="custom-input w-100 mt-1"
            placeholder="Nom du partenaire"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="partner-link" className="text-sm">
            Site web du partenaire{" "}
            <span className="text-danger fst-italic">
              (ex : https://www.exemple.com)
            </span>
          </label>
          <input
            id="partner-link"
            type="url"
            className="custom-input w-100 mt-1"
            placeholder="https://"
            value={link}
            onChange={(e) => setLink(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="partner-logo" className="text-sm">
            Logo du partenaire
          </label>
          <CFormInput
            id="partner-logo"
            type="file"
            className="mt-1"
            ref={logoRef}
          />
          {partner.logoImage && (
            <img
              src={partner.logoImage}
              alt=""
              width={100}
              height={100}
              className="d-block mx-auto mt-3"
              style={{ objectFit: "contain" }}
            />
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="partner-dark-logo" className="text-sm">
            Logo pour le thème sombre
          </label>
          <CFormInput
            id="partner-dark-logo"
            type="file"
            className="mt-1"
            ref={darkLogoRef}
          />
          {hasDark && (
            <div
              className="bg-black p-4 mx-auto mt-3"
              style={{ width: "fit-content", borderRadius: 12 }}
            >
              <img
                src={partner.darkLogoImage}
                alt=""
                width={100}
                height={100}
                style={{ objectFit: "contain" }}
              />
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={save}
          disabled={update.isLoading}
          className="d-flex align-items-center justify-content-center btn btn-danger shadow-primary w-100 mt-2 p-2 fw-bold text-white"
        >
          {update.isLoading ? (
            <>
              <CSpinner size="sm" /> {general.fr.wait}
            </>
          ) : (
            "Enregistrer"
          )}
        </button>
      </CModalBody>
    </CModal>
  );
}

/* ─────────────── Row ─────────────── */

interface RowProps {
  partner: Partner;
  deletedList: React.Dispatch<BulkDeleteActions>;
  isDeleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function PartnerRow({
  partner,
  deletedList,
  isDeleting,
  onEdit,
  onDelete,
}: RowProps) {
  const [isChecked, setIsChecked] = useState(false);

  const toggle = useCallback(
    (checked: boolean) => {
      setIsChecked(checked);
      deletedList({
        type: checked ? DELETE_ACTIONS.ADD : DELETE_ACTIONS.REMOVE,
        id: partner.id,
      });
    },
    [deletedList, partner.id],
  );

  // A row that unmounts (page change, search) must not stay selected
  useEffect(
    () => () => deletedList({ type: DELETE_ACTIONS.REMOVE, id: partner.id }),
    [deletedList, partner.id],
  );

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      <CTableDataCell style={tdStyle}>
        <div
          className="d-flex align-items-center gap-3"
          style={{ minWidth: 220 }}
        >
          <CFormCheck
            checked={isChecked}
            onChange={(e) => toggle(e.target.checked)}
          />
          {partner.logoImage ? (
            <img
              src={partner.logoImage}
              alt=""
              width={40}
              height={40}
              className="flex-shrink-0"
              style={{
                borderRadius: 10,
                objectFit: "contain",
                backgroundColor: "#f7f7f7",
              }}
            />
          ) : (
            <span
              className="d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-bold"
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                color: "#f0813f",
                backgroundColor: "rgba(240,129,63,0.14)",
              }}
            >
              {(partner.name?.[0] ?? "?").toUpperCase()}
            </span>
          )}
          <span className="fw-semibold text-capitalize">{partner.name}</span>
        </div>
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        {partner.link ? (
          <a
            href={partner.link}
            target="_blank"
            rel="noreferrer noopener"
            className="text-truncate d-inline-block"
            style={{ maxWidth: 280 }}
            title={partner.link}
          >
            {cleanUrl(partner.link)}
          </a>
        ) : (
          <Dash />
        )}
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          <IconAction title="Modifier" onClick={onEdit}>
            <EditIcon width="16" height="16" fill={colors.secondary} />
          </IconAction>
          <IconAction
            title="Supprimer"
            danger
            onClick={onDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <CSpinner size="sm" />
            ) : (
              <DeleteIcon width="18" height="18" fill={colors.primary} />
            )}
          </IconAction>
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

/* ─────────────── Table ─────────────── */

interface TableProps {
  Partners: Partner[];
  PartnersToBeDeleted: React.Dispatch<BulkDeleteActions>;
}

export default function PartnersTable({
  Partners,
  PartnersToBeDeleted,
}: TableProps) {
  const { token } = useCookie("vToken");
  const { toastSuccess, toastError } = useToast();
  const queryClient = useQueryClient();
  const [toEdit, setToEdit] = useState<Partner | null>(null);
  const [toDelete, setToDelete] = useState<Partner | null>(null);

  const deleteMutation = useMutation((id: string) =>
    apiService.MakeDeleteRequest(`partners/${id}`, token),
  );

  const confirmDelete = () => {
    if (!toDelete) return;
    deleteMutation.mutate(toDelete.id, {
      onError: () => toastError(general.fr.message.operationFailed),
      onSuccess() {
        toastSuccess(general.fr.message.partnerDeleted);
        queryClient.invalidateQueries("getAllPartners");
      },
    });
    setToDelete(null);
  };

  const deletingId = deleteMutation.isLoading
    ? deleteMutation.variables
    : undefined;

  return (
    <div>
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={thStyle}>Partenaire</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Site web</CTableHeaderCell>
            <CTableHeaderCell style={{ ...thStyle, textAlign: "right" }}>
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {Partners?.map((partner) => (
            <PartnerRow
              key={partner.id}
              partner={partner}
              deletedList={PartnersToBeDeleted}
              isDeleting={deletingId === partner.id}
              onEdit={() => setToEdit(partner)}
              onDelete={() => setToDelete(partner)}
            />
          ))}
        </CTableBody>
      </CTable>

      {toEdit && (
        <EditPartnerModal
          key={toEdit.id}
          partner={toEdit}
          onClose={() => setToEdit(null)}
        />
      )}

      <PromptModal
        open={toDelete !== null}
        onAccept={confirmDelete}
        onClose={() => setToDelete(null)}
      />
    </div>
  );
}
